import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type {
  BossAttackPattern,
  BrainBossState,
  EnemyData,
  GameSprite,
} from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"
import type { VfxManager } from "../vfx-manager"

type BrainFsmState =
  | "entering"
  | "node-shielded"
  | "shielded"
  | "exposed"
  | "chaos"

const HOLD_X = Math.round(GAME_WIDTH * 0.72)
const SHIELDED_Y = Math.round(GAME_HEIGHT * 0.28)
const EXPOSED_Y = Math.round(GAME_HEIGHT * 0.52)

const CHAOS_POSITIONS = [
  Math.round(GAME_HEIGHT * 0.2),
  Math.round(GAME_HEIGHT * 0.5),
  Math.round(GAME_HEIGHT * 0.8),
]

const CHAOS_TELEPORT_MS = 1500
const CHAOS_OPENING_MS = 900

export class BrainEnemy extends StatefulEnemyRuntime<BrainFsmState> {
  private chaosTriggered = false
  private phantomSummonTimer = 8000
  private phantomSummoned = false
  private teleportTimer = 0
  private teleportIndex = -1
  private isTeleporting = false
  private nodesSpawned = false

  constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<BrainFsmState>[] = [
      {
        name: "entering",
        behaviorState: "flying",
        canFire: false,
        update: (ctx) => {
          const factor = 1 - (1 - 0.15) ** (ctx.delta / 16.6)
          ctx.sprite.x = Phaser.Math.Linear(ctx.sprite.x, HOLD_X, factor)
          ctx.sprite.y = Phaser.Math.Linear(
            ctx.sprite.y,
            SHIELDED_Y,
            factor * 0.8,
          )
          ctx.sprite.setAlpha(Phaser.Math.Clamp(ctx.stateTime / 400, 0, 1))
          if (ctx.stateTime >= 1100) return "node-shielded"
          return null
        },
      },
      {
        name: "node-shielded",
        behaviorState: "hovering",
        canFire: true,
        enter: (ctx) => {
          ctx.vfx.sustainedScreenTint(0x06b6d4, 0.2, 1000, 500)
          this.nodesSpawned = false
          // STAGE 1: Core is invulnerable while nodes are up
          ctx.enemyData.ballInteraction = "invulnerable"
        },
        update: (ctx) => {
          this.applyShieldedMotion(ctx)
          this.cycleAttackPattern(ctx, false)

          const brainState = ctx.enemyData.supportState as BrainBossState

          const nodes = ctx.scene.children.list.filter((c) => {
            const s = c as GameSprite
            const ed = s.entityData as EnemyData
            return (
              s.active &&
              ed?.kind === "enemy" &&
              ed.type === "synapseNode" &&
              ed.parentId === ctx.enemyData.id
            )
          })

          if (!this.nodesSpawned && nodes.length === 0) {
            this.nodesSpawned = true
            this.spawnNodes(ctx)
          } else if (
            this.nodesSpawned &&
            nodes.length === 0 &&
            ctx.stateTime > 1500
          ) {
            // If nodes were spawned and are now gone, transition to STAGE 2
            brainState.brainPhase = "shielded"
            return "shielded"
          }

          return null
        },
      },
      {
        name: "shielded",
        behaviorState: "hovering",
        canFire: true,
        enter: (ctx) => {
          this.phantomSummonTimer = 8000
          ctx.vfx.flashScreen(0xffffff, 0.4, 300)
          ctx.sprite.setTint(0xffaa00)
          // STAGE 2: Core has a shield that must be destroyed
          ctx.enemyData.ballInteraction = "shield"
        },
        update: (ctx) => {
          this.applyShieldedMotion(ctx)
          this.cycleAttackPattern(ctx, false)

          this.phantomSummonTimer -= ctx.delta
          if (this.phantomSummonTimer <= 0 && !this.phantomSummoned) {
            this.phantomSummoned = true
            ctx.enemyData.bossSpawnRequest = { type: "humanoid", count: 1 }
          }

          const brainState = ctx.enemyData.supportState as
            | BrainBossState
            | undefined
          // STAGE 3: If shield is gone, transition to exposed
          if (brainState?.brainPhase === "exposed") return "exposed"
          return null
        },
        exit: (ctx) => ctx.sprite.clearTint(),
      },
      {
        name: "exposed",
        behaviorState: "attacking",
        canFire: true,
        enter: (ctx) => {
          // STAGE 3: Core is now vulnerable (armored = take normal damage)
          ctx.enemyData.ballInteraction = "armored"
          if (ctx.scene.anims.exists("anim_brain_exposed")) {
            ctx.sprite.play("anim_brain_exposed", true)
          }
        },
        update: (ctx) => {
          if (ctx.hpRatio < 0.3 && !this.chaosTriggered) {
            this.chaosTriggered = true
            return "chaos"
          }

          this.applyExposedMotion(ctx)
          this.cycleAttackPattern(ctx, true)
          return null
        },
      },
      {
        name: "chaos",
        behaviorState: "attacking",
        canFire: true,
        enter: (ctx) => {
          ctx.enemyData.bossAttackPattern = "rage"
          ctx.enemyData.shootCooldown = 700
          this.teleportTimer = CHAOS_OPENING_MS
          this.teleportIndex = -1
          this.isTeleporting = false
          ctx.scene.cameras.main.shake(600, 0.03)
          ctx.sprite.setTint(0xff00ff)
          if (ctx.scene.anims.exists("anim_brain_exposed")) {
            ctx.sprite.play("anim_brain_exposed", true)
          }
          Phaser.Actions.AddEffectShine(ctx.sprite, {
            duration: 400,
            direction: Math.PI / 4,
            colorFactor: [0.4, 2.0, 0.4, 1],
          })
          ctx.vfx.sustainedScreenTint(0x00ff44, 0.2, 2200, 600)
          ctx.vfx.heatHazePulse(0.022, 2800)
        },
        update: (ctx) => {
          const { sprite, enemyData } = ctx

          this.applyChaosMotion(ctx)

          this.teleportTimer -= ctx.delta
          if (this.teleportTimer <= 0 && !this.isTeleporting) {
            this.teleportTimer = CHAOS_TELEPORT_MS
            const nextIndex = (this.teleportIndex + 1) % CHAOS_POSITIONS.length
            this.teleportIndex = nextIndex
            this.doTeleport(
              sprite,
              ctx.scene,
              CHAOS_POSITIONS[nextIndex],
              ctx.vfx,
            )

            // Gravity Well effect when teleporting
            this.applyGravityWell(ctx)
          }

          if (
            ctx.hpRatio < 0.15 &&
            (enemyData.shootCooldown ?? Number.POSITIVE_INFINITY) > 500
          ) {
            enemyData.shootCooldown = 500
            enemyData.bossAttackPattern = "missiles"
          }

          return null
        },
        exit: (ctx) => ctx.sprite.clearTint(),
      },
    ]

    super(sprite, enemyData, "entering", states)
  }

  public override onSpawn(): void {
    super.onSpawn()
    this.sprite.setAlpha(0)
  }

  private spawnNodes(ctx: EnemyStateContext<BrainFsmState>): void {
    ctx.enemyData.bossSpawnRequest = {
      type: "synapseNode",
      count: 4,
      parentId: ctx.enemyData.id,
    }
  }

  private applyShieldedMotion(ctx: EnemyStateContext<BrainFsmState>): void {
    const { sprite, time } = ctx
    const xWave = Math.sin(time / 900) * 16
    const yWave = Math.sin(time / 720) * 24
    sprite.x = HOLD_X + xWave
    sprite.y = SHIELDED_Y + yWave
    sprite.setAngle(Math.sin(time / 540) * 4)
  }

  private applyExposedMotion(ctx: EnemyStateContext<BrainFsmState>): void {
    const { sprite, time } = ctx
    const xWave = Math.sin(time / 520) * 28
    const yWave = Math.sin(time / 260) * 34
    sprite.x = HOLD_X - 18 + xWave
    sprite.y = EXPOSED_Y + yWave
    sprite.setAngle(Math.sin(time / 200) * 6)
  }

  private applyChaosMotion(ctx: EnemyStateContext<BrainFsmState>): void {
    const { sprite, time } = ctx
    // Large figure-eight pattern with erratic jerky movement
    const xBase = HOLD_X - 150 + Math.sin(time / 2000) * 200
    const yBase = EXPOSED_Y + Math.cos(time / 1000) * 150

    const xJitter = Math.sin(time / 150) * 30
    const yJitter = Math.cos(time / 120) * 20

    sprite.x = xBase + xJitter
    sprite.y = yBase + yJitter
    sprite.setAngle(Math.sin(time / 50) * 15)
  }

  private applyGravityWell(ctx: EnemyStateContext<BrainFsmState>): void {
    ctx.vfx.flashScreen(0x00ff44, 0.1, 100)
    // Find all balls and pull them slightly
    const balls = ctx.scene.children.list.filter((c) => {
      const s = c as GameSprite
      return s.active && s.entityData?.kind === "combatBall"
    })

    for (const ball of balls) {
      const s = ball as GameSprite
      const angle = Phaser.Math.Angle.Between(
        s.x,
        s.y,
        ctx.sprite.x,
        ctx.sprite.y,
      )
      const dist = Phaser.Math.Distance.Between(
        s.x,
        s.y,
        ctx.sprite.x,
        ctx.sprite.y,
      )

      if (dist < 500) {
        const force = ((500 - dist) / 500) * 200
        // Apply force to physics body
        const body = s.body as Phaser.Physics.Arcade.Body
        if (body) {
          body.velocity.x += Math.cos(angle) * force
          body.velocity.y += Math.sin(angle) * force
        }
      }
    }
  }

  private doTeleport(
    sprite: GameSprite,
    scene: Phaser.Scene,
    targetY: number,
    vfx?: VfxManager,
  ): void {
    if (this.isTeleporting) return
    this.isTeleporting = true

    vfx?.sustainedScreenTint(0x00ff44, 0.15, 250, 300)
    vfx?.heatHazePulse(0.014, 700)

    scene.tweens.add({
      targets: sprite,
      alpha: 0,
      scaleX: 0.6,
      scaleY: 0.6,
      duration: 160,
      ease: "Power2",
      onComplete: () => {
        sprite.y = targetY
        scene.tweens.add({
          targets: sprite,
          alpha: 1,
          scaleX: 1,
          scaleY: 1,
          duration: 200,
          ease: "Back.easeOut",
          onComplete: () => {
            this.isTeleporting = false
          },
        })
        scene.cameras.main.shake(180, 0.012)
      },
    })
  }

  private cycleAttackPattern(
    ctx: EnemyStateContext<BrainFsmState>,
    isExposed: boolean,
  ): void {
    const ed = ctx.enemyData
    if (isExposed) {
      const cycleTime = 4000
      const phase = Math.floor((ctx.time % (cycleTime * 5)) / cycleTime)
      const patterns: BossAttackPattern[] = [
        "rage",
        "missiles",
        "spiral",
        "climax",
        "neural-blast",
      ]
      ed.bossAttackPattern = patterns[phase]
      if ((ed.shootCooldown ?? Number.POSITIVE_INFINITY) > 1000) {
        ed.shootCooldown = 1000
      }
    } else {
      const cycleTime = 6000
      const phase = Math.floor((ctx.time % (cycleTime * 3)) / cycleTime)
      const patterns: BossAttackPattern[] = ["spiral", "opening", "missiles"]
      ed.bossAttackPattern = patterns[phase]
    }
  }
}
